import React from "react";
import { Alert, Badge, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTheme } from "styled-components";
import { FormattedMessage, useIntl } from "react-intl";
import {
  DateRange,
  MetricRange,
  ScenarioMetricsEntity,
  Verdict,
} from "../domain/experiment.entity";
import { ExperimentRepository } from "../domain/interfaces/ExperimentRepository";
import { DecisionGates, resolveDecisionGates } from "../domain/decisionGates";
import { useExperimentResults } from "../application/hooks/useExperimentResults";
import { ConfidenceIntervalBar } from "./ConfidenceIntervalBar";
import { messages } from "../experiment.messages";

export interface ScenarioComparisonCardsProps {
  propertyTicker: string;
  experimentId: string;
  // Read-only props from ExperimentResultsPage (US-017), which owns this shared state
  // (Business Rule 8 there) — this component does not own or default it.
  range: MetricRange;
  custom: DateRange | undefined;
  repository: ExperimentRepository;
}

interface ScenarioCardProps {
  side: "A" | "B";
  metrics: ScenarioMetricsEntity;
  isWinner: boolean;
  figureColor: string;
  /** Shared across both cards so the two intervals can be read against each other. */
  intervalDomain: [number, number];
}

// Internal, not exported — mirrors ResultsHeader's own "presentational, no hook, no mutation"
// discipline (US-017).
function ScenarioCard({ side, metrics, isWinner, figureColor, intervalDomain }: ScenarioCardProps) {
  const intl = useIntl();
  const {
    variantTicker,
    conversionRate,
    bookings,
    revenueTotal,
    currency,
    validVisitors,
    excludedVisitors,
    ciLow,
    ciHigh,
    botSuspectVisitors,
  } = metrics;

  // Business Rule 7: `currency` can come back as "" on a `partial` response
  // (`RawMetricsResult.empty()`'s convention) — render with no trailing suffix rather than a
  // dangling space or "undefined".
  const revenueDisplay = currency
    ? `${intl.formatNumber(revenueTotal, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })} ${currency}`
    : intl.formatNumber(revenueTotal, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <Card className="flex-fill" data-testid={`scenario-card-${side}`}>
      <Card.Body>
        {/* No Card.Header — a plain flex row inside Card.Body avoids Bootstrap's default
            Card.Header border/background, keeping the label row visually part of the body. */}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="text-uppercase small text-muted font-weight-bold">
            <FormattedMessage {...messages.results.scenarioLabel} values={{ side }} />
          </div>
          {isWinner && (
            <Badge variant="success">
              <FormattedMessage {...messages.results.winner} />
            </Badge>
          )}
        </div>
        <div className="font-weight-bold mb-3" style={{ fontSize: "1rem" }}>
          {variantTicker}
        </div>

        {/* AC1: the large figure is always conversionRate, styled per side's color token
            (theme.orangeBackground for A, theme.darkBlueBackground for B — Business Rule 2). */}
        <div className="mb-3">
          <div
            style={{ color: figureColor, fontSize: "2.5rem", fontWeight: "bold", lineHeight: 1 }}
            data-testid={`scenario-${side}-figure`}>
            {intl.formatNumber(conversionRate, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            %
          </div>
          <div className="text-muted small">
            <FormattedMessage {...messages.metrics.conversionRate} />
          </div>
          {/* The Wilson interval, drawn on a domain shared with the other card — the point of
              drawing it at all is to let the eye see whether the two intervals overlap. */}
          <div className="mt-2">
            <ConfidenceIntervalBar
              low={ciLow}
              high={ciHigh}
              value={conversionRate}
              domain={intervalDomain}
              color={figureColor}
              testId={`scenario-${side}-ci-bar`}
            />
            <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
              <FormattedMessage
                {...messages.results.conversionInterval}
                values={{
                  low: intl.formatNumber(ciLow, { maximumFractionDigits: 2 }),
                  high: intl.formatNumber(ciHigh, { maximumFractionDigits: 2 }),
                }}
              />
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between">
          <div>
            {/* `validVisitors`, not `visits`: this is the denominator the conversion rate is
                actually computed against. The difference is shown right below it rather than
                silently dropped. */}
            <div className="font-weight-bold" style={{ fontSize: "1.1rem" }}>
              {intl.formatNumber(validVisitors)}
            </div>
            <div className="text-muted small">
              <FormattedMessage {...messages.results.validVisitors} />
            </div>
            {excludedVisitors > 0 && (
              <OverlayTrigger
                placement="bottom"
                overlay={props => (
                  <Tooltip id={`excluded-visitors-${side}`} {...props}>
                    {intl.formatMessage(messages.results.excludedVisitorsTooltip, {
                      excluded: intl.formatNumber(excludedVisitors),
                      bots: intl.formatNumber(botSuspectVisitors),
                    })}
                  </Tooltip>
                )}>
                <div
                  className="text-muted"
                  style={{ fontSize: "0.75rem", cursor: "help" }}
                  data-testid={`scenario-${side}-excluded`}>
                  <FormattedMessage
                    {...messages.results.excludedVisitors}
                    values={{ count: intl.formatNumber(excludedVisitors) }}
                  />
                </div>
              </OverlayTrigger>
            )}
          </div>
          <div>
            <div className="font-weight-bold" style={{ fontSize: "1.1rem" }}>
              {intl.formatNumber(bookings)}
            </div>
            <div className="text-muted small">
              <FormattedMessage {...messages.metrics.bookings} />
            </div>
          </div>
          <div>
            <div className="font-weight-bold" style={{ fontSize: "1.1rem" }}>
              {revenueDisplay}
            </div>
            <div className="text-muted small">
              <FormattedMessage {...messages.metrics.revenueTotal} />
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

interface CenterColumnProps {
  /** The response's own top-level `lift`, in percent (B over A). */
  liftValue: number | null;
  /** Backend-computed (`GetExperimentResultsResponse.is_reliable`), never derived from `verdict`. */
  isReliable: boolean;
  /** `[liftCiLow, liftCiHigh]`, or null when the backend says the interval is not computable. */
  liftInterval: [number, number] | null;
  /** FRACTION in [0,1], as the wire sends it. */
  confidence: number;
  /** The same three gates the decision panel renders, so the two can never disagree about what is
   *  still pending. */
  gates: DecisionGates;
}

/**
 * Names WHICH gate is still open, in place of the old, uninformative "No fiable" badge. The
 * conjunction is a translated string rather than `Intl.ListFormat`, which is not available in
 * every environment this bundle runs in.
 */
function formatPendingItems(gates: DecisionGates, intl: ReturnType<typeof useIntl>): string {
  const items: string[] = [];
  if (!gates.sampleMet) items.push(intl.formatMessage(messages.results.gateNounSample));
  if (!gates.bookingsMet) items.push(intl.formatMessage(messages.results.gateNounBookings));
  if (!gates.durationMet) items.push(intl.formatMessage(messages.results.gateNounDuration));

  if (items.length <= 1) return items[0] ?? "";
  return intl.formatMessage(messages.common.listConjunction, {
    first: items.slice(0, -1).join(", "),
    last: items[items.length - 1],
  });
}

function CenterColumn({
  liftValue,
  isReliable,
  liftInterval,
  confidence,
  gates,
}: CenterColumnProps) {
  const intl = useIntl();

  let liftDisplay: string;
  let liftClassName = "";
  if (liftValue === null) {
    liftDisplay = "—";
  } else if (liftValue === 0) {
    // Business Rule 5: exactly zero renders as a plain, unstyled "0%" — neither green nor red.
    liftDisplay = "0%";
  } else {
    // Signed: the figure answers "B against A", so which way it points is half the information.
    liftDisplay = `${intl.formatNumber(liftValue, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      signDisplay: "exceptZero",
    })}%`;
    liftClassName = liftValue > 0 ? "text-success" : "text-danger";
  }

  const pendingItems = formatPendingItems(gates, intl);

  return (
    <div className="d-flex flex-column align-items-center justify-content-center px-3 gap-2">
      <div className="text-muted text-center" style={{ fontSize: "0.75rem", fontWeight: 600 }}>
        <FormattedMessage {...messages.results.liftLabel} />
      </div>
      <div
        className={liftClassName}
        data-testid="lift-value"
        style={{ fontSize: "2rem", fontWeight: "bold" }}>
        {liftDisplay}
      </div>
      {liftInterval && (
        <div
          className="text-muted text-center"
          style={{ fontSize: "0.75rem" }}
          data-testid="lift-interval">
          <FormattedMessage
            {...messages.results.liftInterval}
            values={{
              low: intl.formatNumber(liftInterval[0], {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
                signDisplay: "exceptZero",
              }),
              high: intl.formatNumber(liftInterval[1], {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
                signDisplay: "exceptZero",
              }),
            }}
          />
        </div>
      )}
      <div
        className="text-muted text-center"
        style={{ fontSize: "0.75rem" }}
        data-testid="lift-confidence">
        <FormattedMessage
          {...messages.results.confidenceLine}
          values={{
            confidence: intl.formatNumber(confidence * 100, { maximumFractionDigits: 0 }),
          }}
        />
      </div>
      {/* Business Rule 4 / Decision 3: always render a badge, never nothing, so the "not yet
          reliable" state doesn't read as still-loading data. Unlike the old "No fiable", it now
          says what the wait is for. */}
      {isReliable ? (
        <Badge variant="success" data-testid="reliability-badge">
          <FormattedMessage {...messages.results.reliable} />
        </Badge>
      ) : (
        <Badge variant="secondary" data-testid="reliability-badge">
          {pendingItems === "" ? (
            // `is_reliable === false` with every gate reported met: the backend knows something
            // this response does not spell out, so the badge stays honest rather than naming a
            // gate that is not actually open.
            <FormattedMessage {...messages.results.notReliable} />
          ) : (
            <FormattedMessage {...messages.results.pendingOf} values={{ items: pendingItems }} />
          )}
        </Badge>
      )}
    </div>
  );
}

export function ScenarioComparisonCards({
  propertyTicker,
  experimentId,
  range,
  custom,
  repository,
}: ScenarioComparisonCardsProps) {
  const theme = useTheme();

  // Self-contained data-fetching section, like every other US-017 mount-point sibling — this
  // component does not receive ExperimentResults data as a prop from the page shell. De-duped
  // against sibling sections via React Query's query-key equality (US-003).
  const { data, isLoading, isError } = useExperimentResults(
    propertyTicker,
    experimentId,
    range,
    repository,
    custom
  );

  if (isLoading) {
    return (
      <div className="text-muted" data-testid="comparison-loading">
        <FormattedMessage {...messages.results.comparisonLoading} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Alert variant="danger" data-testid="comparison-error">
        <FormattedMessage {...messages.results.comparisonError} />
      </Alert>
    );
  }

  const { scenarioA, scenarioB, verdict } = data;

  // `is_reliable` is now a real field on the response and is authoritative. The US-019 client-side
  // derivation (`verdict ∈ {WINNER_A, WINNER_B}`) was always a documented stop-gap for its absence.
  const isReliable = data.isReliable;

  // Business Rule 3 / AC4: the GANADOR badge is sourced exclusively from `verdict`, never from
  // any per-row `MetricComparison.winner`.
  const winningSide: "A" | "B" | null =
    verdict === Verdict.VERDICT_WINNER_A ? "A" : verdict === Verdict.VERDICT_WINNER_B ? "B" : null;

  // The response now carries its own top-level `lift` (B over A, in percent), so the
  // "conversion_rate" table row is no longer the only source.
  const liftValue = data.lift;
  const liftInterval: [number, number] | null = data.hasLiftCi
    ? [data.liftCiLow, data.liftCiHigh]
    : null;

  // One domain for both cards (design.md Decision 3), padded by 10% of its own width so neither
  // extreme sits flush against the track's edge. A degenerate domain is handled inside the bar.
  const rawMin = Math.min(scenarioA.ciLow, scenarioB.ciLow);
  const rawMax = Math.max(scenarioA.ciHigh, scenarioB.ciHigh);
  const padding = (rawMax - rawMin) * 0.1;
  const intervalDomain: [number, number] = [rawMin - padding, rawMax + padding];

  return (
    <div className="d-flex align-items-stretch gap-3">
      <ScenarioCard
        side="A"
        metrics={scenarioA}
        isWinner={winningSide === "A"}
        figureColor={theme.orangeBackground}
        intervalDomain={intervalDomain}
      />
      <CenterColumn
        liftValue={liftValue}
        isReliable={isReliable}
        liftInterval={liftInterval}
        confidence={data.confidence}
        gates={resolveDecisionGates(data)}
      />
      <ScenarioCard
        side="B"
        metrics={scenarioB}
        isWinner={winningSide === "B"}
        figureColor={theme.darkBlueBackground}
        intervalDomain={intervalDomain}
      />
    </div>
  );
}
